import { action } from "mobx";
import { Enum } from "helpers";
import { initialState, templateInitialState } from "./state";
// Array setters
const createArraySetter = (store, key) =>
  action(items => {
    // Reset only the specific array property, not affecting other store properties
    if (Array.isArray(items)) {
      store[key] = [...items];
    } else if (items?.items && Array.isArray(items.items)) {
      store[key] = [...items.items];
    } else if (items === null || items === undefined) {
      store[key] = [];
    } else {
      console.warn(`${key} received invalid data:`, items);
      // Keep the existing array if the items are invalid
      if (!Array.isArray(store[key])) {
        store[key] = [];
      }
    }
  });

// Simple setters
const createSimpleSetter = (store, key) =>
  action(value => {
    store[key] = value;
  });

// Object setters
const createObjectSetter = (store, key, defaultValues = {}) =>
  action(data => {
    store[key] = {};
    if (data && typeof data === "object") {
      store[key] = {
        ...defaultValues,
        ...data
      };
    } else {
      console.warn(`${key} received invalid data:`, data);
      store[key] = { ...defaultValues };
    }
  });

export const createMutations = store => ({

  // landing page setters
  setCurrentPage: createSimpleSetter(store, "currentPage"),
  setPageSize: createSimpleSetter(store, "pageSize"),
  setFilterSearchText: createSimpleSetter(store, "filterSearchText"),
  setFilterModel: createSimpleSetter(store, "filterModel"),
  setIsLoading: createSimpleSetter(store, "isLoading"),
  setRowData: createSimpleSetter(store, "rowData"),
  setShowFilterModal: createSimpleSetter(store, "showFilterModal"),
  setTotalRow: createSimpleSetter(store, "totalRow"),
  setBeePluginLoading: createSimpleSetter(store, "beePluginLoading"),
  setActiveLandingPageTab: createSimpleSetter(store, "activeLandingPageTab"),
  setLandingPageDraftName: createSimpleSetter(store, "landingPageDraftName"),
  setHasChanges: createSimpleSetter(store, "hasChanges"),
  setLandingPageDns: createSimpleSetter(store, "landingPageDns"),
  setLandingPageUrl: createSimpleSetter(store, "landingPageUrl"),
  setLandingPageDescription: createSimpleSetter(store, "landingPageDescription"),
  setLandingPageCategories: createArraySetter(store, "landingPageCategories"),
  setHtml: createSimpleSetter(store, "html"),
  setSubdomain: createSimpleSetter(store, "subdomain"),
  setCompanyCustomDomainId: createSimpleSetter(store, "companyCustomDomainId"),
  setCategory: createSimpleSetter(store, "category"),
  setIsFromTemplate: createSimpleSetter(store, "isFromTemplate"),
  setSubdomains: createArraySetter(store, "subdomains"),
  setIsLoadingSubdomains: createSimpleSetter(store, "isLoadingSubdomains"),
  setCompanySubdomainId: createSimpleSetter(store, "companySubdomainId"),
  setState: createSimpleSetter(store, "state"),
  setGoogleAnalytics: createSimpleSetter(store, "googleAnalytics"),
  // landing page ARRAY setters
  setLandingPages: createArraySetter(store, "landingPages"),

  // landing page object setters
  setLandingPage: createObjectSetter(store, "landingPage"),
  setBeeEditor: createSimpleSetter(store, "beeEditor"),
  setJsonTemplate: createSimpleSetter(store, "jsonTemplate"),
  setOnNextChange: createSimpleSetter(store, "onNextChange"),
  setDefaultState: action(() => {
    Object.keys(initialState).forEach(key => {
      store[key] = initialState[key];
    });
  }),

  // landing page template setters
  setTemplateCurrentPage: createSimpleSetter(store, "templateCurrentPage"),
  setTemplatePageSize: createSimpleSetter(store, "templatePageSize"),
  setTemplateFilterSearchText: createSimpleSetter(store, "templateFilterSearchText"),
  setTemplateFilterModel: createSimpleSetter(store, "templateFilterModel"),
  setTemplateIsLoading: createSimpleSetter(store, "templateIsLoading"),
  setTemplateTotalRow: createSimpleSetter(store, "templateTotalRow"),
  setTemplateShowFilterModal: createSimpleSetter(store, "templateShowFilterModal"),
  setTemplateBeeEditor: createSimpleSetter(store, "templateBeeEditor"),
  setTemplateBeePluginLoading: createSimpleSetter(store, "templateBeePluginLoading"),
  setTemplateActiveLandingPageTab: createSimpleSetter(store, "templateActiveLandingPageTab"),
  setTemplateLandingPageDraftName: createSimpleSetter(store, "templateLandingPageDraftName"),
  setTemplateHasChanges: createSimpleSetter(store, "templateHasChanges"),
  setTemplateLandingPageUrl: createSimpleSetter(store, "templateLandingPageUrl"),
  setTemplateLandingPageDescription: createSimpleSetter(store, "templateLandingPageDescription"),
  setTemplateLandingPageCategories: createArraySetter(store, "templateLandingPageCategories"),
  setIsShowWizardModal: createSimpleSetter(store, "isShowWizardModal"),
  setTemplateDefaultLandingPageTab: createSimpleSetter(store, "templateDefaultLandingPageTab"),
  setTemplateHtml: createSimpleSetter(store, "templateHtml"),
  setTemplateSubdomain: createSimpleSetter(store, "templateSubdomain"),
  setTemplateCategory: createSimpleSetter(store, "templateCategory"),

  // landing page template ARRAY setters
  setTemplateLandingPages: createArraySetter(store, "templateLandingPages"),

  //object setters
  setTemplateLandingPage: createObjectSetter(store, "templateLandingPage"),

  setTemplateDefaultState: action(() => {
    Object.keys(templateInitialState).forEach(key => {
      store[key] = templateInitialState[key];
    });
  }),

  // Bee templates setters
  setBeeTemplates: createArraySetter(store, "beeTemplates"),
  setBeeTemplatesLoading: createSimpleSetter(store, "beeTemplatesLoading"),
  setBeeTemplatesError: createSimpleSetter(store, "beeTemplatesError"),
  setBeeTemplatesPage: createSimpleSetter(store, "beeTemplatesPage"),
  setBeeTemplatesTotalCount: createSimpleSetter(store, "beeTemplatesTotalCount"),
  setBeeTemplatesNext: createSimpleSetter(store, "beeTemplatesNext"),
  setBeeTemplatesPrevious: createSimpleSetter(store, "beeTemplatesPrevious"),
  setBeeTemplatesFacets: createObjectSetter(store, "beeTemplatesFacets"),
  setSelectedBeeTemplateCategories: createArraySetter(store, "selectedBeeTemplateCategories"),
  setSelectedCategories: createArraySetter(store, "selectedCategories"),
  setSearchTerm: createSimpleSetter(store, "searchTerm"),

  // Bee categories setters (like email store)
  setBeeCategories: createArraySetter(store, "beeCategories"),
  setBeeCategoriesLoading: createSimpleSetter(store, "beeCategoriesLoading"),
  setBeeCategoriesError: createSimpleSetter(store, "beeCategoriesError"),
});
